import mapboxgl from 'mapbox-gl';
import { createSupabase, type LocationPublic } from '@tempesta/shared';
import 'mapbox-gl/dist/mapbox-gl.css';
import './styles.scss';
import iconMain from './assets/loc-main.svg';
import iconSec from './assets/loc-sec.svg';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

const supabase = createSupabase({
    url: import.meta.env.VITE_SUPABASE_URL,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
});

const ROME_CENTER: [number, number] = [12.4964, 41.9028];
const INITIAL_ZOOM = 11;
const DESKTOP_FIT_PADDING = 80;
let loadedLocations: LocationPublic[] = [];

const map = new mapboxgl.Map({
    container: 'tls-map',
    style: 'mapbox://styles/latempestasilenziosa/cmoqxgiv3000101r3bsi32gyu',
    center: ROME_CENTER,
    zoom: INITIAL_ZOOM,
    scrollZoom: false,
    attributionControl: false,
});

map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

map.on('load', async () => {
    const locations = await fetchLocations();
    loadedLocations = locations;
    addMarkers(locations);
    fitToLocations(locations);
});

class ResetViewControl implements mapboxgl.IControl {
    private container?: HTMLDivElement;

    constructor(private readonly onReset: () => void) {}

    onAdd(): HTMLDivElement {
        this.container = document.createElement('div');
        this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tls-reset-control';
        button.title = 'Reset vista';
        button.setAttribute('aria-label', 'Reset vista');
        button.addEventListener('click', this.onReset);

        this.container.appendChild(button);
        return this.container;
    }

    onRemove(): void {
        this.container?.remove();
        this.container = undefined;
    }
}

map.addControl(new ResetViewControl(resetMapView), 'top-right');

async function fetchLocations(): Promise<LocationPublic[]> {
    const { data, error } = await supabase
        .from('locations_public')
        .select('id,type,name,is_public,address,participation_info,lng,lat');

    if (error) {
        console.error('[tls] load error:', error);
        return [];
    }
    return (data ?? []) as LocationPublic[];
}

function addMarkers(locations: LocationPublic[]): void {
    for (const loc of locations) {
        const el = document.createElement('div');
        el.className = `tls-marker tls-marker--${loc.type}`;

        const icon = document.createElement('img');
        icon.className = 'tls-marker__icon';
        icon.src = getMarkerIcon(loc);
        icon.alt = '';
        el.appendChild(icon);

        const label = document.createElement('span');
        label.className = 'tls-marker__label';
        label.textContent = loc.name;
        el.appendChild(label);

        const marker = new mapboxgl.Marker({ element: el, anchor: 'left' })
            .setLngLat([loc.lng, loc.lat])
            .addTo(map);
        marker.getElement().classList.add(`tls-marker-shell--${loc.type}`);

        if (loc.address) {
            const popup = new mapboxgl.Popup({ offset: 18, closeButton: false }).setHTML(`
                <div class="tls-popup">
                    <div class="tls-popup__name">${escapeHtml(loc.name)}</div>
                    <div class="tls-popup__addr">${escapeHtml(loc.address)}</div>
                    ${loc.participation_info ? `
                        <div class="tls-popup__separator"></div>
                        <div class="tls-popup__info">${loc.participation_info}</div>
                    ` : ''}
                </div>`);
            marker.setPopup(popup);
        }
    }
}

function getMarkerIcon(loc: LocationPublic): string {
    return loc.type === 'official' ? iconSec : iconMain;
}

function resetMapView(): void {
    if (loadedLocations.length === 0) {
        map.easeTo({ center: ROME_CENTER, zoom: INITIAL_ZOOM, duration: 600 });
        return;
    }

    fitToLocations(loadedLocations, 600);
}

function fitToLocations(locations: LocationPublic[], duration = 0): void {
    if (locations.length === 0) return;

    const bounds = locations.reduce(
        (b, l) => b.extend([l.lng, l.lat]),
        new mapboxgl.LngLatBounds(),
    );
    map.fitBounds(bounds, { padding: getFitPadding(), maxZoom: 14, duration });
}

function getFitPadding(): mapboxgl.PaddingOptions {
    const mapWidth = map.getContainer().clientWidth;

    if (mapWidth <= 767) {
        return {
            top: 56,
            right: Math.min(260, Math.max(180, Math.round(mapWidth * 0.32))),
            bottom: 56,
            left: 40,
        };
    }

    return {
        top: DESKTOP_FIT_PADDING,
        right: 160,
        bottom: DESKTOP_FIT_PADDING,
        left: DESKTOP_FIT_PADDING,
    };
}

function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
    );
}
